"use client";

import classNames from "@casino/external/classNames";
import GameLoadError from "@casino/ui/Games/GameLoadError";
import { useGamePageData } from "@casino/ui/Games/hooks";
import type { AuthorizeHub88SessionMutation } from "@workspace/casino_core/mutations/AuthorizeHub88SessionMutation";
import Log from "@workspace/nextjs_utils/src/Log";
import Script from "next/script";
import type { FC } from "react";
import * as React from "react";
import useFiatRateSync from "./useFiatRateSync";
import useGameBalanceBridge from "./useGameBalanceBridge";
import WebComponentGameLoader from "./WebComponentGameLoader";

// Hub88 games mount a <game-container> custom element into a div we own rather than an iframe.

export type Hub88Session = NonNullable<
  AuthorizeHub88SessionMutation["casinoAuthorizeHub88SessionMutation"]
>;

export type WebComponentGameConfig = {
  session: Hub88Session;
  sdkUrl: string;
  language: string;
  currencyOptions?: CurrencyOptions;
  site?: Site;
};

type Props = WebComponentGameConfig & {
  onReadyChange?: (isReady: boolean) => void;
};

const CONTAINER_ID = "hub88-game-container";

const WebComponentGame: FC<Props> = ({
  session,
  sdkUrl,
  language,
  currencyOptions,
  site,
  onReadyChange,
}) => {
  const { isFullscreen, walletCurrency } = useGamePageData();
  const [isLoaded, setLoaded] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);
  const [attempt, setAttempt] = React.useState(0);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const gameRef = React.useRef<Hub88GameSDK | null>(null);
  const isInitialisingRef = React.useRef(false);

  useGameBalanceBridge({
    containerRef,
    walletCurrency: currencyOptions?.["wallet-currency"],
    attempt,
  });

  useFiatRateSync({
    fiatRate: currencyOptions?.["fiat-rate"],
    isReady: isLoaded,
  });

  const initGame = React.useCallback(async () => {
    if (
      isInitialisingRef.current ||
      gameRef.current ||
      !session.launcherBaseUrl ||
      !window.Hub88Games
    ) {
      return;
    }
    isInitialisingRef.current = true;
    try {
      gameRef.current = await window.Hub88Games.init({
        launcher_base_url: session.launcherBaseUrl,
        provider_pid: session.partnerId,
        provider_user: session.providerUserId,
        provider_token: session.token,
        provider_currency_code: session.providerCurrencyCode,
        containerId: CONTAINER_ID,
        lang: language,
        meta: {
          fiatCurrency: currencyOptions?.["fiat-currency"],
          fiatRate: currencyOptions?.["fiat-rate"],
          walletCurrency: currencyOptions?.["wallet-currency"],
          skin: site === "sportsbet" ? "sportsbet" : "bitcasino",
        },
      });
    } catch (error) {
      Log.error("Hub88 SDK init failed", { error });
      setHasError(true);
    } finally {
      isInitialisingRef.current = false;
    }
  }, [session, language, currencyOptions, site]);

  const handleScriptReady = () => {
    initGame();
  };

  const handleScriptError = () => {
    Log.error("Hub88 SDK script failed to load", { sdkUrl });
    setHasError(true);
  };

  const restartGame = () => {
    gameRef.current?.destroy();
    gameRef.current = null;
    setLoaded(false);
    onReadyChange?.(false);
    setHasError(false);
    setAttempt((n) => n + 1);
  };

  React.useEffect(() => {
    initGame();
  }, [initGame, attempt]);

  const previousWalletCurrency = React.useRef(walletCurrency);

  React.useEffect(() => {
    if (previousWalletCurrency.current === walletCurrency) return;
    previousWalletCurrency.current = walletCurrency;
    restartGame();
  }, [walletCurrency]);

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleReady = () => {
      setLoaded(true);
      onReadyChange?.(true);
    };

    const handleFailed = () => setHasError(true);
    container.addEventListener("game-ready", handleReady);
    container.addEventListener("load-failed", handleFailed);
    return () => {
      container.removeEventListener("game-ready", handleReady);
      container.removeEventListener("load-failed", handleFailed);
    };
  }, [attempt, onReadyChange]);

  React.useEffect(
    () => () => {
      gameRef.current?.destroy();
      gameRef.current = null;
    },
    [],
  );

  const wrapperClasses = classNames(
    "relative h-full w-full overflow-hidden",
    !isFullscreen && "rounded-lg",
  );

  if (hasError) {
    return (
      <div className={wrapperClasses}>
        <GameLoadError onRetry={restartGame} />
      </div>
    );
  }

  return (
    <div key={attempt} className={wrapperClasses}>
      <Script
        id="hub88-games-sdk"
        type="module"
        crossOrigin="anonymous"
        src={sdkUrl}
        onReady={handleScriptReady}
        onError={handleScriptError}
      />
      <WebComponentGameLoader isHidden={isLoaded} site={site} />
      <div
        id={CONTAINER_ID}
        ref={containerRef}
        data-testid="hub88-container"
        className="h-full w-full"
      />
    </div>
  );
};

export default WebComponentGame;
